import type { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import { Flex } from "@redotech/redo-web/flex";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { useCustomerOrders } from "../hooks/use-customer-orders";
import { useCustomerReturns } from "../hooks/use-customer-returns";
import { AssigneeSelector } from "./assignee-selector";
import {
  ChannelBadge,
  PriorityBadge,
  StatusBadge,
} from "./badges/conversation-badges";
import { ConversationDetailsSection } from "./conversation-details-section";
import { ConversationTicketFieldTitle } from "./conversation-ticket-field-title";
import { CustomerInfoSection } from "./customer-info-section";
import { OrderCard } from "./order-card";
import { ReturnCard } from "./return-card";
import { TagSelector } from "./tag-selector";

export interface ConversationDetailsPanelProps {
  conversation: ConversationListItemResponse;
}

/**
 * Right panel component that displays conversation details.
 * Shows overview (status, priority) and customer info placeholders.
 */
export const ConversationDetailsPanel = memo(function ConversationDetailsPanel({
  conversation,
}: ConversationDetailsPanelProps) {
  return (
    <Flex dir="column" flex={1} gap="lg" overflow="auto" p="xl">
      <ConversationOverviewSection conversation={conversation} />

      <CustomerInfoSection conversation={conversation} />

      <OrdersSection conversation={conversation} />

      <ReturnsSection conversation={conversation} />
    </Flex>
  );
});

interface ConversationOverviewSectionProps {
  conversation: ConversationListItemResponse;
}

const ConversationOverviewSection = memo(function ConversationOverviewSection({
  conversation,
}: ConversationOverviewSectionProps) {
  return (
    <ConversationDetailsSection
      localStorageKey="redo.support-v2.conversation-details.ticket.collapsed"
      title="Ticket"
    >
      <AssigneeSelector
        conversationId={conversation.id}
        mongoAssigneeId={conversation.mongoAssigneeId}
      />
      {conversation.priority && (
        <Flex align="center" gap="md" justify="space-between">
          <ConversationTicketFieldTitle>Priority</ConversationTicketFieldTitle>
          <PriorityBadge priority={conversation.priority} />
        </Flex>
      )}
      <Flex align="center" gap="md" justify="space-between">
        <ConversationTicketFieldTitle>Status</ConversationTicketFieldTitle>
        <StatusBadge status={conversation.status} />
      </Flex>
      <Flex align="center" gap="md" justify="space-between">
        <ConversationTicketFieldTitle>Channel</ConversationTicketFieldTitle>
        {conversation.channels.map((channel) => (
          <ChannelBadge channel={channel} key={channel} size="sm" />
        ))}
      </Flex>
      <Flex align="center" gap="md" justify="space-between">
        <ConversationTicketFieldTitle>Created At</ConversationTicketFieldTitle>
        <Text fontSize="xs" textColor="tertiary">
          {conversation.lastCustomerMessageAt
            ? conversation.lastCustomerMessageAt.toLocaleString(undefined, {
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              })
            : "-"}
        </Text>
      </Flex>
      <Flex align="center" gap="md" justify="space-between">
        <ConversationTicketFieldTitle>
          Last Response
        </ConversationTicketFieldTitle>
        <Text fontSize="xs" textColor="tertiary">
          {conversation.lastCustomerMessageAt
            ? conversation.lastCustomerMessageAt.toLocaleString(undefined, {
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              })
            : "-"}
        </Text>
      </Flex>
      <TagSelector
        conversationId={conversation.id}
        currentTagIds={conversation.tagIds}
      />
    </ConversationDetailsSection>
  );
});

interface OrdersSectionProps {
  conversation: ConversationListItemResponse;
}

const OrdersSection = memo(function OrdersSection({
  conversation,
}: OrdersSectionProps) {
  const customerEmail = conversation.customer?.email;
  const { orders, pending, error } = useCustomerOrders(customerEmail);

  return (
    <ConversationDetailsSection
      localStorageKey="redo.support-v2.conversation-details.orders.collapsed"
      title="Orders"
    >
      {pending ? (
        <Flex align="center" justify="center" p="md">
          <LoadingRedoAnimation />
        </Flex>
      ) : error ? (
        <Text fontSize="xs" textColor="error">
          Failed to load orders
        </Text>
      ) : orders.length === 0 ? (
        <Text fontSize="xs" textColor="tertiary">
          No orders found
        </Text>
      ) : (
        <Flex dir="column" gap="md">
          {orders.map((order) => (
            <OrderCard key={order._id} order={order} />
          ))}
        </Flex>
      )}
    </ConversationDetailsSection>
  );
});

interface ReturnsSectionProps {
  conversation: ConversationListItemResponse;
}

const ReturnsSection = memo(function ReturnsSection({
  conversation,
}: ReturnsSectionProps) {
  const customerEmail = conversation.customer?.email;
  const { returns, pending, error } = useCustomerReturns(customerEmail);

  return (
    <ConversationDetailsSection
      localStorageKey="redo.support-v2.conversation-details.returns.collapsed"
      title="Returns"
    >
      {pending ? (
        <Flex align="center" justify="center" p="md">
          <LoadingRedoAnimation />
        </Flex>
      ) : error ? (
        <Text fontSize="xs" textColor="error">
          Failed to load returns
        </Text>
      ) : returns.length === 0 ? (
        <Text fontSize="xs" textColor="tertiary">
          No returns found
        </Text>
      ) : (
        <Flex dir="column" gap="md">
          {returns.map((returnData) => (
            <ReturnCard key={returnData._id} return={returnData} />
          ))}
        </Flex>
      )}
    </ConversationDetailsSection>
  );
});
